import {Box, Divider, Tab, Tabs, Typography} from '@mui/material';
import {
  TabData,
  TabPanelProps,
} from '../../../user-management/edit-user/EditUser';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  Fragment,
} from 'react';
import {t} from 'i18next';
import './DocumentCenterPanel.scss';
import {ReactComponent as MaximizeIcon} from '../../../../assets/images/maximize.svg';
import {ReactComponent as MinimizeIcon} from '../../../../assets/images/minimize.svg';
import {ReactComponent as PopinIcon} from '../../../../assets/images/pop-in-icon.svg';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import ArrTooltip from '../../../common/arr-tooltip/ArrTooltip';
import DocNavigation from '../../doc-navigation/DocNavigation';
import MainPanelToolbar, {
  ToolbarAction,
  ToolbarActionType,
} from '../../doc-navigation/main-panel-toolbar/MainPanelToolbar';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import DocumentViewerPanel from './DocumentViewerPanel/DocumentView';
import {
  useGetAISummaryForDocument,
  useGetDuplicateDocumentsForSelectedDocumentId,
  useGetFilePath,
  useGetTranslations,
  useMultipleConceptResults,
} from '../../../../hooks/document/useDocument';
import {AnnotationMenu} from './AnnotationMenu/AnnotationMenu';
import {baseGetDuplicateDocsPayload, MouseToolType} from '../../docViewerUtils';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {RedactionMenu} from './redaction-menu/RedactionMenu';
import {ISelectItem} from '../../../../shared/types';
import {DocumentMediaViewerPanel} from './DocumentViewerPanel/DocumentMediaViewerPanel';
import TextDocTab from './text-doc-tab/TextDocTab';
import {DOC_TYPE} from '../../../../shared/constants';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../../common/arr-menu/ArrMenu';
import {TranslationItem} from '../../../../api/types/document.type';
import ConcurrentUserWarning from './ConcurrentUserWarning/ConcurrentUserWarning';
import {useConcurrentWarning} from './ConcurrentUserWarning/useConcurrentWarning';
import ArrButton from '../../../common/arr-button/ArrButton';
import {usePortalPopoutManager} from '../../../PortalPopout';
import DocSummary from '../../doc-summary/DocSummary';
import {ReactComponent as SuccessIcon} from '../../../../assets/images/success-icon.svg';
import {setHasRnfvFiles} from '../../../../redux/slice/docViewSlice';

const TabPanel = (props: Readonly<TabPanelProps>) => {
  const {children, value, index, ...other} = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      className={`tabpanel-${value === index ? 'active' : 'hidden'}`}
      {...other}
      data-testid={`document-center-panel-div-tab-${index}`}
    >
      {value === index && (
        <div data-testid={`document-center-panel-div-tab-child-${index}`}>
          {children}
        </div>
      )}
    </div>
  );
};
const enDocViewHeaderTabs = 'docView.headers.mainPanelTabs';
const initialActiveTab = 0;
const RESPONSIVE_BREAKPOINT = 720;

const enum FILE_TYPE {
  default = 'VIEWER',
  text = 'TEXT',
  images = 'TIFF',
  translation = 'TRANSLATION',
}
export type PageData = {
  totalPages: number;
  pageNumber: number;
};

type DocumentCenterPanelProps = {
  showExpandSections: boolean;
  expandCollapseOnClick: () => void;
  centerWidth: number;
  codingFormUpdated: boolean;
};

const DocumentCenterPanel: React.FC<DocumentCenterPanelProps> = ({
  showExpandSections,
  expandCollapseOnClick,
  centerWidth,
  codingFormUpdated,
}) => {
  const {
    activeDocument,
    panels = {
      leftPanels: [],
      rightPanels: [],
      popoutsOpen: [],
      fixedPanels: false,
    },
  } = useSelector((state: RootState) => ({
    activeDocument: state.docView.activeDoc,
    panels: state.docView.panels,
  }));

  const isViewer = useMemo(
    () =>
      activeDocument &&
      activeDocument?.fileTypeCode?.toLowerCase() === 'viewer',
    [activeDocument?.fileTypeCode]
  );
  const menuRef = useRef<ArrMenuHandle>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const [menuItems, setMenuItems] = useState<CustomMenuItem[]>([]);
  const [tabValue, setTabValue] = useState(initialActiveTab);
  const [isResponsiveMode, setIsResponsiveMode] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [toolbarOptionSelected, setToolbarOptionSelected] = useState('');
  const [selectionMode, setSelectionMode] = useState<string | null>(null);
  const [viewerControl, setViewerControl] = useState<ViewerControl | null>(
    null
  );
  const [viewerReady, setViewerReady] = useState<boolean>(false);
  const [pageData, setPageData] = useState<PageData>({
    totalPages: 0,
    pageNumber: 0,
  });
  const [initialScaleFactor, setInitialScaleFactor] = useState<number | null>(
    null
  );
  const [isReady, setIsReady] = useState<boolean>(false);
  const [textSelection, setTextSelection] = useState<any>(null);
  const [fileType, setFileType] = useState<string>(FILE_TYPE.default);
  const [translationSeqNo, setTranslationSeqNo] = useState<string>('');
  const [translations, setTranslations] = useState<TranslationItem[]>([]);
  const [selectedTranslations, setSelectedTranslations] =
    useState<TranslationItem>();
  const dispatch = useDispatch();
  const {showWarning} = useConcurrentWarning();
  const {
    defaultDocView,
    productionView,
    isAllowPDFPrinting,
    isAllowNativeDownloads,
  } = useSelector((state: RootState) => state.docView.docViewConfiguration);

  const TAB_VALUE = useMemo(() => {
    const bothDisabled = !defaultDocView && !productionView;
    const oneDisabled = !defaultDocView || !productionView;
    return {
      defaultTab: defaultDocView ? 0 : -1,
      textTab: defaultDocView ? 1 : 0,
      imagesTab: productionView ? (defaultDocView ? 2 : 1) : -1,
      translationTab: bothDisabled ? 1 : oneDisabled ? 2 : 3,
    };
  }, [defaultDocView, productionView]);

  const isTranslationTab = tabValue === TAB_VALUE.translationTab;
  const isImagesTab = tabValue === TAB_VALUE.imagesTab;

  const {data: translationsData, isLoading: isTranslationsLoading} =
    useGetTranslations(
      Number(activeDocument?.documentId ?? '0'),
      fileType,
      (isTranslationTab || isImagesTab) && !!activeDocument?.documentId
    );

  const [searchLoading, setSearchLoading] = useState(false);
  const [manualSearchResults, setManualSearchResults] = useState<
    SearchResult[]
  >([]);
  const [searchFn, setSearchFn] = useState<
    | ((
        vc: ViewerControl,
        searchTerm: string,
        isPersistentSearch: boolean
      ) => void)
    | null
  >(null);

  const isMaximized =
    !panels.fixedPanels &&
    [...panels.leftPanels, ...panels.rightPanels].length ===
      panels.popoutsOpen.length;
  const popouts = usePortalPopoutManager();

  const translationsSignature = useMemo(
    () =>
      (translationsData?.translations ?? [])
        .map((translation) => `${translation.name}:${translation.seqNo ?? ''}`)
        .join('|'),
    [translationsData?.translations]
  );

  useEffect(() => {
    const availableTranslations = translationsData?.translations ?? [];

    if (availableTranslations.length > 0) {
      const firstTranslation = availableTranslations[0];

      setTranslationSeqNo(firstTranslation.seqNo ?? '');
      setTranslations(availableTranslations);
      setSelectedTranslations(firstTranslation);
      setMenuItems(
        availableTranslations.map((translation) => ({
          id: translation.name,
          label: translation.name,
          selected: translation.name === firstTranslation.name,
        }))
      );
    } else {
      setTranslationSeqNo('-1');
      setSelectedTranslations(undefined);
      setTranslations([]);
      setMenuItems([]);
    }
  }, [translationsSignature]);

  const shouldFetchFilePath = useMemo(() => {
    if (!isViewer || !activeDocument?.documentId) return false;

    if (fileType === FILE_TYPE.translation || fileType === FILE_TYPE.images) {
      return !isTranslationsLoading;
    }

    return true;
  }, [activeDocument?.documentId, fileType, isViewer, isTranslationsLoading]);
  useEffect(() => {
    setSelectionMode('');
  }, [toolbarOptionSelected]);

  const {data: docData, isLoading: isFilePathFetching} = useGetFilePath(
    {
      documentId: Number(activeDocument?.documentId ?? '0'),
      documentName: activeDocument?.fileName ?? '',
      fileType,
      seqNo:
        fileType === FILE_TYPE.translation &&
        translationSeqNo &&
        translationSeqNo !== '-1'
          ? translationSeqNo
          : undefined,
      contextType:
        fileType === FILE_TYPE.images && selectedTranslations?.typeCode
          ? selectedTranslations.typeCode
          : undefined,
      productionId:
        fileType === FILE_TYPE.images && selectedTranslations?.contextID
          ? Number(selectedTranslations.contextID)
          : undefined,
    },
    shouldFetchFilePath
  );

  const hasRnfvFiles = docData?.hasRnfvFiles ?? false;
  dispatch(setHasRnfvFiles(hasRnfvFiles));

  const documentId = Number(activeDocument?.documentId ?? 0);

  const {data: aiSummary} = useGetAISummaryForDocument(
    {
      docIds: [documentId],
    },
    documentId !== 0
  );

  const hasAIsummary = aiSummary?.documentSummaries[0]?.summary;

  const payload = useMemo(() => {
    if (activeDocument && docData?.docID) {
      const concepts = activeDocument?.ConceptParameters?.split('``');
      return (
        concepts?.map((parameter) => ({
          docId: `${docData?.docID}`,
          conceptParameter: parameter,
          scorePercentage: activeDocument?.MinConceptScore ?? '',
        })) ?? []
      );
    }
    return [];
  }, [activeDocument, docData]);

  const results = useMultipleConceptResults(payload);

  const isConceptsLoading = useMemo(() => {
    const loadingStates = results.map((result) => result.isLoading);
    return !!loadingStates.find((state) => !!state);
  }, [results]);

  const annotationLayerId = useSelector(
    (state: RootState) => state.docView.docViewConfiguration.annotationLayerId
  );
  const {data: duplicateDocs, isLoading: isDuplicateDocsLoading} =
    useGetDuplicateDocumentsForSelectedDocumentId(
      {
        ...baseGetDuplicateDocsPayload,
        annotationLayerId: isNaN(Number(annotationLayerId))
          ? 1
          : Number(annotationLayerId),
        documentExt: docData?.documentExtension ?? '',
        documentName: activeDocument?.fileName ?? '',
        documentId: Number(activeDocument?.documentId) ?? 0,
      },
      !!docData?.documentExtension && !isNaN(Number(annotationLayerId))
    );

  useEffect(() => {
    if (
      viewerReady &&
      viewerControl &&
      viewerControl._cs && // Check whether _cs is available at runtime
      typeof viewerControl?.fitContent === 'function'
    ) {
      viewerControl?.fitContent(window.PCCViewer.FitType.FullWidth);
    }
  }, [centerWidth, viewerReady, viewerControl]);

  useEffect(() => {
    // Check whether _cs is available at runtime
    if (viewerControl && viewerReady && viewerControl._cs) {
      if (
        selectionMode === MouseToolType.RectangleAnnotation ||
        selectionMode === MouseToolType.RectangleRedaction ||
        selectionMode === MouseToolType.TextSelectionRedaction
      ) {
        viewerControl?.setCurrentMouseTool(selectionMode);
      } else {
        switch (toolbarOptionSelected) {
          case ToolbarActionType.Remark:
            viewerControl?.setCurrentMouseTool(
              MouseToolType.HighlightAnnotation
            );
            break;
          case ToolbarActionType.CopyText:
            viewerControl?.setCurrentMouseTool(MouseToolType.SelectText);
            break;
          default:
            viewerControl?.setCurrentMouseTool(MouseToolType.PanAndEdit);
        }
      }
    }
  }, [selectionMode, viewerControl, viewerReady, toolbarOptionSelected]);

  const handleChangeTab = (
    event: React.SyntheticEvent,
    newTabValue: number
  ) => {
    setTabValue(newTabValue);
    if (newTabValue === TAB_VALUE.defaultTab) {
      setFileType(FILE_TYPE.default);
    } else if (newTabValue === TAB_VALUE.translationTab) {
      setFileType(FILE_TYPE.translation);
    } else if (newTabValue === TAB_VALUE.imagesTab) {
      setFileType(FILE_TYPE.images);
      setAnchorEl(event.currentTarget as HTMLElement);
      menuRef.current?.open(event.currentTarget as HTMLElement);
    }

    if (newTabValue !== TAB_VALUE.translationTab) {
      setTranslationSeqNo('');
    }

    setIsReady(false);
    setViewerReady(false);
    setViewerControl(null);
    setSelectionMode(null);
    setToolbarOptionSelected('');
  };

  const handleImageMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    tabId: string
  ) => {
    if (tabId == 'images-tab') {
      setAnchorEl(event.currentTarget);
      menuRef.current?.open(event.currentTarget);
    }
  };

  const handleImageMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSelectDocument = (item: CustomMenuItem) => {
    setIsReady(false);
    setViewerReady(false);
    setViewerControl(null);
    setSelectedTranslations(
      translations.find((translation) => translation.name === item.id) ??
        selectedTranslations
    );

    setMenuItems((prevItems) =>
      prevItems.map((menuItem) => ({
        ...menuItem,
        selected: menuItem.id === item.id,
      }))
    );
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        setIsResponsiveMode(width < RESPONSIVE_BREAKPOINT);
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const docTypeValue = useMemo(() => {
    return docData?.documentFileSourceType?.toLowerCase() ?? '';
  }, [docData?.documentFileSourceType]);

  useEffect(() => {
    setSelectionMode(null);
    setViewerControl(null);
    setViewerReady(false);
    setTranslationSeqNo('');
  }, [activeDocument?.documentId]);

  const isLoading = useMemo(
    () =>
      isFilePathFetching ||
      isDuplicateDocsLoading ||
      isTranslationsLoading ||
      isConceptsLoading,
    [
      isDuplicateDocsLoading,
      isFilePathFetching,
      isTranslationsLoading,
      isConceptsLoading,
    ]
  );

  const isMedia = useMemo(
    () => activeDocument?.fileTypeCode?.toLowerCase() === 'media',
    [activeDocument?.fileTypeCode]
  );

  const renderDefaultContext = useCallback(() => {
    if (!activeDocument) return <Box />;

    if (isMedia && !isTranslationTab && !isImagesTab)
      return <DocumentMediaViewerPanel />;

    if (isLoading) return <ArrLoadingSpinner overlay show isOpen />;

    return (
      <DocumentViewerPanel
        docData={docData}
        setViewerControl={setViewerControl}
        setViewerReady={setViewerReady}
        duplicateDocs={`${duplicateDocs ?? activeDocument.documentId}`}
        setInitialScaleFactor={setInitialScaleFactor}
        setIsReady={setIsReady}
        setPageData={setPageData}
        isLoading={isLoading}
        setTextSelection={setTextSelection}
        selectionMode={selectionMode ?? ''}
        selectedToolbarOption={toolbarOptionSelected}
        isTranslations={isTranslationTab}
        setSearchFn={setSearchFn}
        setManualSearchResults={setManualSearchResults}
        setSearchLoading={setSearchLoading}
        isImageTab={isImagesTab}
        showWarning={showWarning}
        conceptResults={results.map((result) => result.data)}
      />
    );
  }, [
    activeDocument,
    docData,
    duplicateDocs,
    isLoading,
    isMedia,
    selectionMode,
    fileType,
    toolbarOptionSelected,
    isTranslationTab,
    isImagesTab,
    setSearchFn,
    setManualSearchResults,
    setSearchLoading,
    showWarning,
    results,
  ]);

  const renderDefaultSubTitle = useCallback(() => {
    let subTitle: React.ReactNode = (
      <span data-testid={`document-center-panel-span`}>&nbsp;</span>
    );
    if (isMedia) {
      subTitle = t(`${enDocViewHeaderTabs}.media`);
    } else if (docTypeValue) {
      subTitle = t(`${enDocViewHeaderTabs}.${docTypeValue}`);
    }
    return (
      <Typography
        className={`typo-text label4_size sub-label doc-type-${isMedia ? 'media' : docTypeValue}`}
        data-testid={`document-center-panel-subtitle-text`}
      >
        {subTitle}
      </Typography>
    );
  }, [docTypeValue, isMedia]);

  const tabData: TabData[] = useMemo(
    () =>
      [
        {
          id: 'default-tab',
          label: (
            <ArrTooltip
              content={
                docTypeValue
                  ? t(`${enDocViewHeaderTabs}.${docTypeValue}Tooltip`)
                  : isMedia
                    ? t(`${enDocViewHeaderTabs}.mediaTooltip`)
                    : ''
              }
            >
              <Typography
                className="typo-text subhead4_size tab-label"
                data-testid="default-tab-label"
              >
                {t(`${enDocViewHeaderTabs}.default`)}
              </Typography>
              {renderDefaultSubTitle()}
            </ArrTooltip>
          ),
          content: renderDefaultContext(),
          enabled: defaultDocView,
        },
        {
          id: 'text-tab',
          label: (
            <Typography
              className="typo-text subhead4_size tab-label"
              data-testid="text-tab-label"
            >
              {t(`${enDocViewHeaderTabs}.txt`)}
            </Typography>
          ),
          content: (
            <TextDocTab docId={Number(activeDocument?.documentId ?? '0')} />
          ),
          enabled: true,
        },
        {
          id: 'images-tab',
          label: (
            <Typography
              className="typo-text subhead4_size tab-label"
              data-testid="images-tab-label"
            >
              {t(`${enDocViewHeaderTabs}.images`)}
            </Typography>
          ),
          //   Will remove once implemented
          content: renderDefaultContext(),
          enabled: productionView,
        },
        {
          id: 'translation-tab',
          label: (
            <Typography
              className="typo-text subhead4_size tab-label"
              data-testid="translation-tab-label"
            >
              {t(`${enDocViewHeaderTabs}.translation`)}
            </Typography>
          ),
          content: renderDefaultContext(),
          enabled: true,
        },
      ].filter((tab) => tab.enabled),
    [
      activeDocument?.documentId,
      docTypeValue,
      isMedia,
      renderDefaultContext,
      renderDefaultSubTitle,
      defaultDocView,
      productionView,
    ]
  );

  const handleToolbarOptionChange = useCallback((action: ToolbarAction) => {
    setToolbarOptionSelected(action.toolbarActionType);
  }, []);

  const totalPages =
    viewerReady && viewerControl && viewerControl._cs
      ? viewerControl?.getPageCount()
      : 0;

  const onFullPageHighlight = useCallback(async () => {
    // Check whether _cs is available at runtime
    if (viewerControl && viewerReady && viewerControl._cs) {
      const currentPage = viewerControl.getPageNumber();
      const size = await viewerControl.requestPageAttributes(currentPage);
      const mark = viewerControl.addMark(currentPage, 'RectangleAnnotation');
      // Fill entire page
      mark.setRectangle({x: 0, y: 0, width: size.width, height: size.height});
      mark.setFillColor('#FFFF00'); // black box
      mark.setBorderColor('##FFFF00'); // no visible border contrast
      mark.setBorderThickness(1);
      mark.setFontColor('##FFFF00');
    }
  }, [viewerControl, viewerReady]);

  const onFullPageRedact = useCallback(
    async (redactionTagSelection: ISelectItem) => {
      if (viewerControl && viewerReady) {
        const currentPage = viewerControl.getPageNumber();
        const size = await viewerControl.requestPageAttributes(currentPage);
        const mark = viewerControl.addMark(currentPage, 'RectangleRedaction');
        mark.setRectangle({x: 0, y: 0, width: size.width, height: size.height});
        mark.setBorderThickness(1);
        // Legacy colors for sightline
        mark.setBorderColor('#000000');
        mark.setFillColor('#000000');
        mark.setData('NewRedaction', 'true');
        mark.setData(
          'RedactionTagID',
          redactionTagSelection?.id ? `${redactionTagSelection?.id}` : ''
        );
      }
    },
    [viewerControl, viewerReady]
  );

  const onMultiPageRedact = useCallback(
    async (redactionTagSelection: ISelectItem, pages: number[]) => {
      if (viewerControl && viewerReady) {
        pages.forEach(async (item, index) => {
          const size = await viewerControl.requestPageAttributes(item);
          const mark = viewerControl.addMark(item, 'RectangleRedaction');
          mark.setRectangle({
            x: 0,
            y: 0,
            width: size.width,
            height: size.height,
          });
          mark.setBorderThickness(1);
          // Legacy colors for sightline
          mark.setBorderColor('#000000');
          mark.setFillColor('#000000');
          mark.setData('NewRedaction', 'true');
          index === pages.length - 1 && mark.setData('IsLastPage', 'true');
          mark.setData(
            'RedactionTagID',
            redactionTagSelection?.id ? `${redactionTagSelection?.id}` : ''
          );
        });
      }
    },
    [viewerControl, viewerReady]
  );

  useEffect(() => {
    // Check whether _cs is available at runtime
    if (viewerControl && viewerControl._cs && viewerControl?.deselectMarks) {
      viewerControl.deselectMarks(viewerControl?.getSelectedMarks());
    }
  }, [selectionMode]);

  useEffect(() => {
    if (hasRnfvFiles && toolbarOptionSelected === ToolbarActionType.Redact) {
      setToolbarOptionSelected('');
    }
  }, [hasRnfvFiles, toolbarOptionSelected]);

  const isActiveTextDocTab = tabValue !== TAB_VALUE.textTab;

  return (
    <Box ref={containerRef} className="document-view-tab">
      <Box className="tab-header-box">
        <Tabs
          value={tabValue || 0}
          onChange={handleChangeTab}
          aria-label="tabs"
          className="tabs"
        >
          {tabData.map((tab) => (
            <Tab
              id={tab.id}
              aria-controls={`tabpanel-${tab.id}`}
              key={tab.id}
              label={tab.label}
              className="tab"
              data-testid={tab.id}
              onClick={(e) => handleImageMenuOpen(e, tab.id)}
              aria-expanded={
                tab.id === 'images-tab' ? Boolean(anchorEl) : undefined
              }
              data-tab-group="center-panel"
              data-tab-id={`center-panel-tab-${tab.id}`}
            />
          ))}
          <ArrMenu
            ref={menuRef}
            items={menuItems}
            onClose={handleImageMenuClose}
            direction="bottom-left"
            className="image-tab-menu"
            onItemClick={handleSelectDocument}
            enableTooltip
            id="center-panel-menu"
            data-tab-group="center-panel"
            data-tab-id="center-panel-menu"
          />
        </Tabs>
        {isMaximized ? (
          <div
            className="restore-button"
            data-testid={`document-center-panel-restore`}
          >
            <ArrButton
              dataTestId="restore-button"
              aria-label={t('docView.headers.restore')}
              variant="secondary"
              size="small"
              startIcon={<PopinIcon />}
              onClick={() => popouts.closeAll()}
            >
              {t('docView.headers.restore')}
            </ArrButton>
          </div>
        ) : (
          <div
            className="expand-button"
            data-testid={`document-center-panel-collapse-expand-button`}
          >
            <ArrIconButton
              icon={showExpandSections ? <MinimizeIcon /> : <MaximizeIcon />}
              onClick={expandCollapseOnClick}
              ariaLabel={
                showExpandSections
                  ? t('docView.headers.collapse')
                  : t('docView.headers.maximize')
              }
              tooltipContent={
                showExpandSections
                  ? t('docView.headers.collapse')
                  : t('docView.headers.maximize')
              }
              tooltipPlacement="bottom"
              testId="expand-collapsed-button"
              tooltipKey={showExpandSections ? 'expanded' : 'collpased'}
            />
          </div>
        )}
      </Box>
      {!isLoading && (
        <Fragment>
          <DocNavigation
            isResponsiveMode={isResponsiveMode}
            isMedia={isMedia}
            isShowDownload={isActiveTextDocTab && isAllowNativeDownloads}
            isShowPrint={isActiveTextDocTab && isAllowPDFPrinting}
            isTranslationTab={isTranslationTab}
            isImageTab={isImagesTab}
            disabledDownload={
              isImagesTab &&
              translations.filter((t) => t.seqNo !== '-1').length === 0
            }
            selectedTranslation={selectedTranslations}
            codingFormUpdated={codingFormUpdated}
          />
          <Divider className="toolbar-divider" />
        </Fragment>
      )}
      {isActiveTextDocTab && (
        <>
          {isViewer && isReady && (
            <>
              <MainPanelToolbar
                isResponsiveMode={isResponsiveMode}
                onToolbarActionChange={handleToolbarOptionChange}
                viewerControl={viewerControl}
                initialScaleFactor={initialScaleFactor ?? 1}
                pageData={pageData}
                isReady={isReady}
                textSelection={textSelection}
                tabValue={tabValue}
                searchFn={searchFn}
                manualSearchResults={manualSearchResults}
                searchLoading={searchLoading}
                disableSearch={docTypeValue === DOC_TYPE.TIFF}
                isImagesTab={isImagesTab}
                isTranslationTab={isTranslationTab}
                initialActiveButton={toolbarOptionSelected || null}
                hasRnfvFiles={hasRnfvFiles}
              />
              {aiSummary?.isSummarizationEnabled && (
                <DocSummary
                  isDisabled={!hasAIsummary || !isReady}
                  title={t('docView.aiGeneratedSummary')}
                >
                  {hasAIsummary}
                </DocSummary>
              )}
            </>
          )}
          {hasRnfvFiles && !isTranslationTab && !isImagesTab && (
            <Box
              className="rnfv-mapped-message"
              data-testid="rnfv-mapped-message"
            >
              <SuccessIcon />
              <Typography className="body3_size">
                {t('docView.rnfvRestrictions.mappedMessage')}
              </Typography>
            </Box>
          )}
          {(toolbarOptionSelected === ToolbarActionType.Annotate ||
            toolbarOptionSelected === ToolbarActionType.Redact ||
            toolbarOptionSelected === ToolbarActionType.Remark) && (
            <ConcurrentUserWarning showWarning={showWarning} />
          )}
          {toolbarOptionSelected === ToolbarActionType.Annotate && (
            <AnnotationMenu
              selectionMode={selectionMode}
              setSelectionMode={setSelectionMode}
              onFullPage={onFullPageHighlight}
              showWarning={showWarning}
            />
          )}
          {toolbarOptionSelected === ToolbarActionType.Redact && (
            <RedactionMenu
              selectionMode={selectionMode}
              setSelectionMode={setSelectionMode}
              onFullPage={onFullPageRedact}
              onMultiPage={onMultiPageRedact}
              totalPages={totalPages}
              showWarning={showWarning || hasRnfvFiles}
            />
          )}
        </>
      )}
      {tabData.map((tab, index) => (
        <TabPanel key={tab.id} value={tabValue || 0} index={index}>
          {tab.content}
        </TabPanel>
      ))}
    </Box>
  );
};
export default React.memo(DocumentCenterPanel);
